import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, InputAdornment,
  List, ListItemButton, ListItemText, MenuItem, Radio, RadioGroup, Stack, TextField, Typography
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { apiPost } from '../generic/Api_Functions'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { fetchLanguageCatalogue } from '../generic/utilities/languageCatalogue'
import LanguageTexts from './languageTexts/LanguageTexts'

/**
 * Historias 138 and 176 — the "Custom languages" tab of the Backoffice, in two columns (DC-29, RN6m).
 *
 * Left, 260 px fixed: the "+ New language" button, which opens the registration form of story 138 in a
 * dialog, and the list of the custom languages of this installation. Right, the rest of the width: the
 * detail of the picked language's texts (`LanguageTexts`, story 176), where its terms are redefined one by
 * one, undone, restored and their history read; an invitation (T42) when none is picked, or T77 when there
 * is no custom language yet. Below `md` the two columns stack.
 *
 * ⚠️ What the screen still does NOT do with a language (S61, S19): edit it or remove it. Picking one to
 * read and redefine its texts is allowed; changing the language itself is not. The list holds ONLY the
 * custom languages (DC-9): the standard ones are not shown here (the language selector of the top bar
 * is the catalogue that lists them). With no custom language at all the right column says so (T77) and
 * points at "+ New language"; there is no detail. The file the server writes can also be edited by hand,
 * which is the reason its content is served with no cache.
 *
 * ⚠️ **No dictionary travels, which is the decision worth understanding (amendment 4, S17).** The
 * registration sends four fields -- code, name, base, whether it replaces -- and nothing else. The
 * file is born EMPTY on the server, and the language runs on its base through i18next's own
 * hierarchy (`es-acme → es → en`) until somebody redefines a key by hand. It used to attach the
 * whole base dictionary from memory: 3.275 keys, 175 KB per registration, and a registration the
 * product owner watched freeze the application for twenty minutes. Nobody needs a copy of what the
 * application already carries inside, so nothing is copied anywhere.
 *
 * ⚠️ A custom language can never be the base of another, so the base list is the catalogue filtered
 * to the standard ones -- never a list written here by hand.
 */

/**
 * Route of the registration.
 *
 * ⚠️ **Not under `/backoffice/**` any more** (amendment 3, 2026-09-18). It used to be, and that door
 * only opened for the literal user `smartpm` while this very screen is shown to anyone whose
 * **profile** is `SUPERADMIN` (`BackofficeMenuItem.jsx:18`, `routes/MainBackoffice.jsx:17`). So a
 * superadmin by profile could see the screen and be refused by the server -- a mismatch the story
 * had signed off as an accepted limit until the product owner ran into it while testing and
 * reverted it. Both doors now look at the same thing, and S60 became a POSITIVE scenario.
 *
 * The refusal of that door still arrives **without a body**, so `refusalKeyFor` keeps its two steps.
 */
const REGISTER_ROUTE = 'languages/custom'

/**
 * How long the registration is given before the screen gives up on it (amendment 4, S17c).
 *
 * ⚠️ Not tuning: the safety net. `apiPost` has no time limit of its own, and a server that takes
 * twenty minutes to answer is twenty minutes of a dead screen with no way out -- which is what
 * happened on 2026-09-18. A four-field registration should take milliseconds; thirty seconds is
 * long enough for any honest answer and short enough that the superadmin is told instead of left
 * staring.
 */
const REGISTER_TIMEOUT_MS = 30000

/**
 * What the superadmin is told when the server refuses, one sentence per reason.
 *
 * The gap that made this impossible is closed: the frozen contract used to answer a refusal with
 * `{ message }` and **no code**, so the eight distinct reasons the scenario enumerates could not be
 * told apart -- one 409 covered two of them and one 400 covered six. Raised rather than papered
 * over, and amended (ENMIENDA 1); the twelve symbols are fixed in `contrato-sun.md` §4. The
 * provisional map by HTTP status that stood here is gone.
 *
 * These are Jupiter's own sentences, not the server's: `message` is developer text and is shown, if
 * at all, as the detail underneath.
 */
const REFUSAL_BY_CODE = {
  LANGUAGE_CODE_ALREADY_EXISTS: 'That language code is already in use',
  INVALID_LANGUAGE_CODE: 'That language code is not valid: use lowercase letters, and a single hyphen before the suffix',
  MISSING_REQUIRED_FIELD: 'Fill in the name and say whether it replaces the base language',
  BASE_LANGUAGE_NOT_FOUND: 'That base language is not in the catalogue',
  BASE_LANGUAGE_NOT_STANDARD: 'The base language has to be a standard one: a custom language cannot be the base of another',
  INVALID_LANGUAGE_CONTENT: 'The content of the base language could not be read, so there is nothing to register',
  LANGUAGE_FILE_WRITE_FAILED: 'The language could not be registered because its file could not be written on the server',
  BASE_LANGUAGE_ALREADY_REPLACED: 'That base language is already replaced by another custom language',
  LANGUAGE_CODE_BASE_MISMATCH: 'The code has to begin with the code of the base language'
}

/**
 * ⚠️ **The second step is not laziness, it is the contract.** Every refusal of this route carries
 * `{ code, message }` -- except one. `NotAuthorizedToManageLanguages` (401/403) comes out of the
 * security configuration that already existed, **before** this story's controller runs, so it
 * carries **no body at all**.
 *
 * A map written on `code` alone -- which is exactly what the amendment invites -- would fall through
 * to the generic sentence precisely when the superadmin needs to be told they lack the permission,
 * and it would do it with no visible error. Hence two declared steps: the code when it comes, the
 * HTTP status for the door that never sends one.
 */
const REFUSAL_BY_STATUS = {
  401: 'You do not have permission to register languages',
  403: 'You do not have permission to register languages'
}

const GENERIC_REFUSAL = 'The language could not be registered'

// V10: a row of the list has to look selectable before it is hovered — an outline, a chevron and a
// hand cursor — and the picked one has to stand apart from that outline, not only by its background.
const SELECTABLE_ROW_SX = {
  mb: 0.5,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  cursor: 'pointer',
  '&:hover': { backgroundColor: 'action.hover' },
  '&.Mui-focusVisible': { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: '2px' },
  '&.Mui-selected': { borderColor: 'primary.main' }
}

/**
 * What the superadmin is told when the server refuses.
 *
 * Translated by `code`, never by reading the sentence the server writes: that sentence is developer
 * text -- it names the offending code or the required prefix -- and hanging eight translations off
 * it would break the day anyone rewrites it.
 *
 * @param {Object} error Error as `Api_Functions` hands it over
 * @returns {string} The i18n key of the sentence to show
 */
const refusalKeyFor = (error) => {
  const code = error?.data?.code
  return REFUSAL_BY_CODE[code] || REFUSAL_BY_STATUS[error?.status] || GENERIC_REFUSAL
}

/**
 * @param {Object} props
 * @param {function(Array<Object>): void} [props.onCatalogueChanged] Receives the catalogue read again
 *   after a registration, so the language selector of the top bar -- which reads it once per session --
 *   offers the new language without a reload
 */
const CustomLanguages = ({ onCatalogueChanged }) => {
  const { t_ } = useSmartTranslation()
  const [catalogue, setCatalogue] = useState([])
  const [baseLanguageCode, setBaseLanguageCode] = useState('')
  const [suffix, setSuffix] = useState('')
  const [name, setName] = useState('')
  // No default on purpose: replacing a language is not something to fall into by leaving a control
  // alone, so nothing is submitted until the superadmin has said which it is.
  const [replacesBase, setReplacesBase] = useState('')
  const [refusal, setRefusal] = useState('')
  const [serverDetail, setServerDetail] = useState('')
  const [registered, setRegistered] = useState('')
  const [saving, setSaving] = useState(false)
  // DC-8: the language whose texts are open below the list; '' = none picked yet.
  const [selectedCode, setSelectedCode] = useState('')
  // DC-29: the registration of the #138 lives in a dialog opened by "+ New language" (T76).
  const [registrationOpen, setRegistrationOpen] = useState(false)

  const loadCatalogue = useCallback(async () => {
    const freshCatalogue = await fetchLanguageCatalogue()
    setCatalogue(freshCatalogue)
    return freshCatalogue
  }, [])

  useEffect(() => {
    loadCatalogue()
  }, [loadCatalogue])

  // A custom language can never be the base of another, so only the standard ones are offered.
  const baseOptions = catalogue.filter((language) => language?.custom !== true)
  const customLanguages = catalogue.filter((language) => language?.custom === true)

  const code = baseLanguageCode === '' ? '' : `${baseLanguageCode}-${suffix}`
  const complete = baseLanguageCode !== '' && suffix !== '' && name !== '' && replacesBase !== ''

  // DC-32 (O-7e): every opening starts from an empty form and without the previous refusal.
  const openRegistration = () => {
    setBaseLanguageCode('')
    setSuffix('')
    setName('')
    setReplacesBase('')
    setRefusal('')
    setRegistrationOpen(true)
  }

  const register = async () => {
    setSaving(true)
    setRefusal('')
    setServerDetail('')
    const registeredCode = code
    try {
      // Four fields and nothing else: the dictionary does not travel (amendment 4). The request is
      // bounded, and the timer is cleared as soon as the server answers so it cannot abort a request
      // that already finished.
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), REGISTER_TIMEOUT_MS)
      try {
        await apiPost(REGISTER_ROUTE, {
          code,
          name,
          baseLanguageCode,
          replacesBase: replacesBase === 'true'
        }, {}, controller.signal)
      } finally {
        clearTimeout(timer)
      }
      setRegistered(name)
      setBaseLanguageCode('')
      setSuffix('')
      setName('')
      setReplacesBase('')
      // The new language has to show up in the list, which is the catalogue read again -- and in the
      // selector of the top bar, which only reads it once per session. Only here and not on the first
      // read: a first read that degraded to the standard eight would drop the session's custom ones.
      const freshCatalogue = await loadCatalogue()
      onCatalogueChanged?.(freshCatalogue)
      // DC-29: on success the dialog closes and the new language is the one picked, so its catalogue opens.
      setRegistrationOpen(false)
      setSelectedCode(registeredCode)
    } catch (error) {
      // The form keeps everything the superadmin typed: making them write it again is punishing them
      // for the server's answer.
      // A registration that ran out of time carries no server sentence: the generic refusal is the
      // whole message, and the browser's own abort text is not shown.
      setRefusal(t_(refusalKeyFor(error)))
      setServerDetail(error?.name === 'AbortError' ? '' : (error?.message || error?.data?.message || ''))
      setRegistered('')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap={3} sx={{ p: 3 }}>
      <Typography variant='h6'>{t_('Custom languages')}</Typography>

      {registered !== ''
        ? <Alert severity='success' onClose={() => setRegistered('')}>{t_('Language registered')}</Alert>
        : null}

      {/* DC-32 (O-7a): while it saves, Escape and a click outside are ignored, like Cancel. */}
      <Dialog open={registrationOpen} onClose={() => { if (!saving) setRegistrationOpen(false) }} fullWidth maxWidth='sm'>
        <DialogTitle>{t_('Register language')}</DialogTitle>
        <DialogContent>
          {refusal !== ''
            ? (
              <Alert severity='error' sx={{ mb: 2 }} onClose={() => setRefusal('')}>
                {refusal}
                {serverDetail !== '' ? <Box sx={{ mt: 0.5, opacity: 0.85 }}>{serverDetail}</Box> : null}
              </Alert>
              )
            : null}
          <Stack gap={2} sx={{ pt: 1 }}>
            <TextField
              select
              label={t_('Base language')}
              value={baseLanguageCode}
              onChange={(event) => setBaseLanguageCode(event.target.value)}
              size='small'
            >
              {baseOptions.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.name || language.code.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>

            {/* The prefix is shown, not typed: the code always begins with its base language, and the
                superadmin should not have to remember to repeat it. The server validates it again
                anyway -- the screen helping does not remove the server's own refusal. */}
            <TextField
              label={t_('Language code')}
              value={suffix}
              onChange={(event) => setSuffix(event.target.value)}
              disabled={baseLanguageCode === ''}
              size='small'
              InputProps={{
                startAdornment: baseLanguageCode === ''
                  ? null
                  : <InputAdornment position='start'>{`${baseLanguageCode}-`}</InputAdornment>
              }}
            />

            <TextField
              label={t_('Name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              size='small'
            />

            <FormControl>
              <FormLabel>{t_('Does it replace the base language?')}</FormLabel>
              <RadioGroup value={replacesBase} onChange={(event) => setReplacesBase(event.target.value)}>
                <FormControlLabel
                  value='false'
                  control={<Radio />}
                  label={t_('No, it is offered alongside it')}
                />
                <FormControlLabel
                  value='true'
                  control={<Radio />}
                  label={t_('Yes, whoever chooses the base language sees this one')}
                />
              </RadioGroup>
            </FormControl>

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setRegistrationOpen(false)}>{t_('Cancel')}</Button>
          <Button variant='contained' disabled={!complete || saving} onClick={register}>
            {t_('Register language')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DC-29 (RN6m): two columns -- the list on the left at a fixed 260 px, the catalogue (or its
          invitation) on the right taking the rest -- stacked below `md` (900 px). */}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems='flex-start'>
        <Box data-testid='custom-languages-left' sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
          <Button variant='outlined' sx={{ mb: 2 }} onClick={openRegistration}>{t_('+ New language')}</Button>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>{t_('Custom languages of this installation')}</Typography>
          {/* S79, RN6m: the list scrolls inside the column when it does not fit, so the button above stays put. */}
          <List dense role='listbox' aria-label={t_('Custom languages of this installation')} sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {customLanguages.map((language) => (
              <ListItemButton
                key={language.code}
                role='option'
                aria-selected={language.code === selectedCode}
                selected={language.code === selectedCode}
                onClick={() => setSelectedCode(language.code)}
                sx={SELECTABLE_ROW_SX}
              >
                <ListItemText
                  primary={`${language.name || language.code} (${language.code})`}
                  secondary={t_('Custom language')}
                />
                <ChevronRightIcon aria-hidden='true' fontSize='small' />
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Box data-testid='custom-languages-right' sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {customLanguages.length > 0 && selectedCode === ''
            ? <Typography>{t_('Select a custom language to edit its texts')}</Typography>
            : null}
          {customLanguages.length === 0
            ? <Typography>{t_('There are no custom languages yet. Create one with "+ New language".')}</Typography>
            : null}
          {selectedCode !== ''
            ? <LanguageTexts key={selectedCode} code={selectedCode} catalogue={catalogue} />
            : null}
        </Box>
      </Stack>
    </Stack>
  )
}

export default CustomLanguages
